import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Alert, CircularProgress } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { apiFetch } from '../api/apiFetch';
import { apiErrorMessage } from '../api/apiError';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: 'rgba(255, 255, 255, 0.9)',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(144, 202, 249, 0.7)' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(144, 202, 249, 0.9)' },
};

/**
 * ACCESS-1 — shared "request access" lead-capture form. Premium is admin-granted
 * via Clerk publicMetadata (Stripe self-serve billing is deferred, ADR-007); this
 * form posts to the UNGATED POST /api/access-requests endpoint so an anonymous
 * visitor or a signed-in free user can ask to be upgraded.
 *
 * Deliberately Clerk-free: `userEmail` arrives as an optional prop (prefills the
 * field but it stays editable) so this component renders standalone — no
 * ClerkProvider, no router — for both callers and tests. Any caller that wants a
 * Clerk-sourced prefill must resolve it itself (useUser) and pass it down; this
 * file must never import from @clerk/*.
 */
export default function RequestAccessForm({ feature, userEmail = '', compact = false }) {
    const [email, setEmail] = useState(userEmail);
    const [message, setMessage] = useState('');
    const [website, setWebsite] = useState(''); // honeypot — real users never see/fill this
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // userEmail can resolve asynchronously (e.g. a Clerk-backed parent mounts before
    // the session loads) — keep the field in sync until the user edits it themselves.
    useEffect(() => {
        if (userEmail) setEmail(userEmail);
    }, [userEmail]);

    const handleSubmit = async () => {
        setError(null);

        const trimmedEmail = email.trim();
        if (!EMAIL_RE.test(trimmedEmail)) {
            setError('A valid email is required');
            return;
        }

        setSubmitting(true);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
            const res = await apiFetch(`${backendUrl}/api/access-requests`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    email: trimmedEmail,
                    message: message.trim() || undefined,
                    feature: feature || undefined,
                    website,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(apiErrorMessage(res.status, data, 'Request failed. Please try again.'));
            } else {
                setSuccess(true);
            }
        } catch (e) {
            setError('Network error. Check your connection and retry.');
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <Alert severity="success" sx={{ py: 0.5 }}>
                Request sent — we will be in touch.
            </Alert>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 1 : 1.5, width: '100%' }}>
            <TextField
                label="Email"
                type="email"
                required
                fullWidth
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                inputProps={{ maxLength: 255 }}
                sx={inputSx}
            />
            {!compact && (
                <TextField
                    label="Message (optional)"
                    multiline
                    minRows={2}
                    fullWidth
                    size="small"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={submitting}
                    inputProps={{ maxLength: 1000 }}
                    sx={inputSx}
                />
            )}

            {/* Honeypot: real visitors never see or tab into this. A non-empty value
                tells the backend to silently drop the request (still 201s, so bots
                learn nothing). Off-screen + zero-opacity rather than display:none so
                unsophisticated bots that skip CSS still find and fill it. */}
            <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0, overflow: 'hidden' }}
            />

            {error && (
                <Alert severity="error" sx={{ py: 0.5 }}>
                    {error}
                </Alert>
            )}

            <Button
                onClick={handleSubmit}
                variant="contained"
                size="small"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                sx={{ textTransform: 'none', alignSelf: compact ? 'flex-start' : 'auto' }}
            >
                {submitting ? 'Sending…' : 'Request access'}
            </Button>
        </Box>
    );
}
